// frontend/src/services/firebaseAuth.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  sendEmailVerification,
  sendPasswordResetEmail,
  updatePassword,
  onAuthStateChanged
} from 'firebase/auth';
import { auth } from '../config/firebase';

/**
 * Register a new user with email and password
 */
export const registerUser = async (email, password) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  // Send verification email
  await sendEmailVerification(userCredential.user);
  return userCredential.user;
};

/**
 * Register a new user WITHOUT sending Firebase verification email
 * Use this when you've already verified the email via OTP
 */
export const registerUserWithoutVerification = async (email, password) => {
  const userCredential = await createUserWithEmailAndPassword(auth, email, password);
  // No email verification sent - assuming already verified via OTP
  return userCredential.user;
};

/**
 * Login user with email and password
 */
export const loginUser = async (email, password) => {
  const userCredential = await signInWithEmailAndPassword(auth, email, password);
  return userCredential.user;
};

/**
 * Logout current user
 */
export const logoutUser = async () => {
  await signOut(auth);
};

/**
 * Send password reset email
 */
export const resetPassword = async (email) => {
  await sendPasswordResetEmail(auth, email);
};

/**
 * Update user password (when logged in)
 */
export const changePassword = async (newPassword) => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user logged in');
  await updatePassword(user, newPassword);
};

/**
 * Resend email verification
 */
export const resendVerificationEmail = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('No user logged in');
  await sendEmailVerification(user);
};

/**
 * Get current user's ID token
 */
export const getCurrentUserToken = async () => {
  const user = auth.currentUser;
  if (!user) return null;
  return await user.getIdToken();
};

/**
 * Subscribe to auth state changes
 */
export const onAuthChange = (callback) => {
  return onAuthStateChanged(auth, callback);
};

/**
 * Get current user
 */
export const getCurrentUser = () => {
  return auth.currentUser;
};
